/**
 * Alterações do dia.
 *
 * Cada entrada tem ator e instante — é o registro que o PRD exige. E é
 * deliberadamente simples: não é auditoria corporativa, não guarda versões e
 * não restaura. O design diz isso na própria tela, para não criar expectativa.
 *
 * As ações do usuário entram no topo em tempo real, porque o store registra
 * histórico em toda mutação.
 */
import { Avatar, EmptyState, ScreenHeader } from '../../ui/primitives';
import { usePessoas } from './people';
import { useItinerary } from './store';

/** Rótulo relativo a partir do instante real, não de texto fixo. */
function quando(at: number, agora: number): string {
  const minutos = Math.floor((agora - at) / 60_000);
  if (minutos < 1) return 'agora';
  if (minutos < 60) return `há ${minutos} min`;

  const horas = Math.floor(minutos / 60);
  if (horas < 24) {
    const hora = new Date(at).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return `Hoje às ${hora}`;
  }

  const dias = Math.floor(horas / 24);
  return dias === 1 ? 'Ontem' : `há ${dias} dias`;
}

export function HistoricoScreen() {
  const history = useItinerary((s) => s.history);
  const { avatarDe, nomeDe } = usePessoas();
  const agora = Date.now();

  return (
    <section className="screen">
      <ScreenHeader title="Alterações do dia" />

      {history.length === 0 ? (
        <EmptyState
          ilustracao="atividade"
          title="Nada mudou ainda"
          description="Conclusões, edições e reordenações aparecem aqui, com quem fez e quando."
        />
      ) : (
        <div className="rot-list">
          {history.map((entry) => (
            <div key={entry.id} className="hist-row">
              <Avatar person={avatarDe(entry.authorId)} size={30} />
              <span className="hist-row__body">
                <span className="hist-row__author">{nomeDe(entry.authorId)}</span>
                <span className="hist-row__desc">{entry.description}</span>
              </span>
              <time className="hist-row__at" dateTime={new Date(entry.at).toISOString()}>
                {quando(entry.at, agora)}
              </time>
            </div>
          ))}
        </div>
      )}

      <p className="dia-footer">
        Este é um registro simples de quem mudou o quê. Não guarda versões nem
        permite restaurar.
      </p>
    </section>
  );
}
